import { IncorporationRequestDataType } from '@commenda/types';
import { PencilLight } from '@commenda/icons';
import { SupportedEntityType } from '@commenda/types/src/corporation';
import { WorkRequestResponseType } from '@commenda/types/src/work-request/types';
import { getCorporationNameFromWorkRequest } from '@commenda/utils';
import { Country, State } from 'country-state-city';
import { SummaryFieldKS } from '../common/SummaryFieldKS';

const determineCompanyStartSellDate = (
  requestData: IncorporationRequestDataType
) => {
  if (requestData?.hasNoSuchStartSellDate) {
    return 'N/A';
  }
  if (!requestData?.hasDifferentDateInTheFuture) {
    return "Same as company's incorporation date";
  }
  if (requestData?.hasDifferentDateInTheFuture) {
    return requestData?.companyStartBuySellDate;
  }
  return 'N/A';
};

type SummaryCompanyInfoProps = {
  data: WorkRequestResponseType & { requestData: IncorporationRequestDataType };
  hideActionButtons?: boolean;
  setActiveStep: (itemIndex: number) => void;
  currentCorporationDetails: SupportedEntityType;
};

export const SummaryCompanyInfo = ({
  data,
  hideActionButtons,
  setActiveStep,
  currentCorporationDetails,
}: SummaryCompanyInfoProps) => {
  const currentAddress = data?.addresses?.find(
    (add) => add.type === 'OperatingAddress'
  ).value;

  const mailingAddress = data?.addresses?.find(
    (add) => add.type === 'MailingAddress'
  )?.value;

  const registerLocation = data?.requestData?.registrationLocation;

  const email = data?.requestData?.companyRegisteredEmailAddress;

  const convertingToLimitedCompanyName =
    data?.requestData?.convertingToLimitedCompanyName;

  const formCT61 = data?.requestData?.hasCT16Payments;
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h4
          className="body-16-bold text-text-primary
        "
        >
          Company Info
        </h4>
        {hideActionButtons ? null : (
          <PencilLight
            className="cursor-pointer"
            onClick={() => setActiveStep(0)}
          />
        )}
      </div>
      <div className="flex flex-col gap-3 bg-bg-disabled-subtle rounded-sm">
        <div className="flex flex-col gap-2 p-6">
          <SummaryFieldKS
            heading="Entity Name 1"
            value={getCorporationNameFromWorkRequest(data?.requestData)}
          />
          <SummaryFieldKS
            heading="Entity Name 2"
            value={getCorporationNameFromWorkRequest(
              data?.requestData,
              'companyLegalName2'
            )}
          />
          <SummaryFieldKS
            heading="Entity Name 3"
            value={getCorporationNameFromWorkRequest(
              data?.requestData,
              'companyLegalName3'
            )}
          />
          <SummaryFieldKS
            heading="Corporation Type"
            value={currentCorporationDetails?.appCopy?.corporationTypeShortName}
          />
          <SummaryFieldKS heading="Company email" value={email} />

          {registerLocation && (
            <SummaryFieldKS
              heading="Registration location"
              value={registerLocation}
            />
          )}

          {convertingToLimitedCompanyName ? (
            <SummaryFieldKS
              heading="LLC to convert"
              value={convertingToLimitedCompanyName}
            />
          ) : null}

          {formCT61 !== undefined ? (
            <SummaryFieldKS
              heading="Will make or receive payments in the first 3 months that require income tax reporting"
              value={formCT61 ? 'Yes' : 'No'}
            />
          ) : null}

          <SummaryFieldKS
            heading="Company sell goods or services start date"
            value={determineCompanyStartSellDate(data?.requestData)}
          />
          <SummaryFieldKS
            heading="Description"
            value={data?.requestData?.businessDescription}
          />
          {mailingAddress && (
            <SummaryFieldKS
              heading="Physical address (communication address)"
              value={[
                `${mailingAddress?.addressLine1}, ${mailingAddress?.addressLine2 ?? ''}`,
                `${mailingAddress?.city} - ${mailingAddress?.postalCode}`,
                `${
                  State.getStateByCodeAndCountry(
                    mailingAddress?.state,
                    mailingAddress?.country
                  )?.name
                }, ${Country.getCountryByCode(mailingAddress?.country)?.name}`,
              ]}
            />
          )}
          {!data?.requestData?.hasOptedForUKMailingAddress &&
          data?.requestData?.hasPrincipalPlaceOfBusiness !== false ? (
            <>
              <SummaryFieldKS
                heading="Principal place of business"
                value={
                  currentAddress?.addressLine1 ===
                    mailingAddress?.addressLine1 &&
                  currentAddress?.addressLine2 ===
                    mailingAddress?.addressLine2 &&
                  currentAddress?.city === mailingAddress?.city &&
                  currentAddress?.state === mailingAddress?.state &&
                  currentAddress?.postalCode === mailingAddress?.postalCode &&
                  currentAddress?.country === mailingAddress?.country
                    ? 'Same as the registered address'
                    : currentAddress
                      ? [
                          `${currentAddress?.addressLine1}, ${currentAddress?.addressLine2 ?? ''}`,
                          `${currentAddress?.city} - ${currentAddress?.postalCode}`,
                          `${
                            State.getStateByCodeAndCountry(
                              currentAddress?.state,
                              currentAddress?.country
                            )?.name
                          }, ${Country.getCountryByCode(currentAddress?.country)?.name}`,
                        ]
                      : 'Different from the registered address'
                }
              />
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};
